import React, { useState } from "react";
import { replicateVijilization } from "services/project";
import Toast from "components/common/Toast";

/**
 * Extract the vijilization ID from a URL or raw ID string.
 * Supports formats like:
 *   - http://localhost:5001/project/.../vijilize/685bebe969cc9193ca56dc2d/heatmap
 *   - http://localhost:5001/project/.../vijilize/685bebe969cc9193ca56dc2d
 *   - 685bebe969cc9193ca56dc2d  (raw ID)
 */
const extractVijilizeId = (input: string): string | null => {
  const trimmed = input.trim();

  // Try to extract from URL pattern: /vijilize/<id>
  const match = trimmed.match(/\/vijilize\/([a-fA-F0-9]{24})/);
  if (match) return match[1];

  // Check if the whole input is a raw 24-char hex ID
  if (/^[a-fA-F0-9]{24}$/.test(trimmed)) return trimmed;

  return null;
};

const ReplicateVijilization: React.FC = () => {
  const [sourceInput, setSourceInput] = useState("");
  const [targetInput, setTargetInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleReplicate = async () => {
    const sourceId = extractVijilizeId(sourceInput);
    const targetId = extractVijilizeId(targetInput);

    if (!sourceId) {
      Toast.error("Invalid Source", "Could not extract a valid vijilization ID from the source URL.");
      return;
    }
    if (!targetId) {
      Toast.error("Invalid Target", "Could not extract a valid vijilization ID from the target URL.");
      return;
    }
    if (sourceId === targetId) {
      Toast.error("Same IDs", "Source and target vijilization IDs cannot be the same.");
      return;
    }

    try {
      setIsSubmitting(true);
      Toast.info("Replicating", "Replication in progress...");

      const response: any = await replicateVijilization(sourceId, targetId);

      if (response?.data) {
        Toast.success("Success", "Vijilization replicated successfully.");
        setSourceInput("");
        setTargetInput("");
      } else {
        Toast.error("Error", response?.message || "Replication failed.");
      }
    } catch (error: any) {
      console.error("Replication error:", error);
      Toast.error("Error", error?.message || "Failed to replicate vijilization.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const sourceId = extractVijilizeId(sourceInput);
  const targetId = extractVijilizeId(targetInput);
  const canSubmit = !!sourceId && !!targetId && sourceId !== targetId && !isSubmitting;

  return (
    <div className="w-full max-w-2xl">
      <h3 className="text-lg font-interSemiBold text-[#101828] mb-1">
        Replicate Vijilization
      </h3>
      <p className="text-sm text-[#667085] mb-6">
        Copy all comparison data from one vijilization to another. Paste the full
        URL or the vijilization ID.
      </p>

      {/* Source */}
      <div className="mb-5">
        <label className="text-[#2D2B32] text-sm font-interMedium block pb-2">
          Copy From (Source)
        </label>
        <input
          type="text"
          className="w-full border border-[#D0D5DD] rounded-lg px-3.5 py-2.5 text-sm text-[#344054] placeholder:text-[#7F7D83] focus:outline-none focus:ring-1 focus:ring-[#1E0AF5] focus:border-[#1E0AF5]"
          placeholder="Paste source vijilization URL or ID"
          value={sourceInput}
          onChange={(e) => setSourceInput(e.target.value)}
        />
        {sourceInput && (
          <p className={`text-xs mt-1 ${sourceId ? "text-green-600" : "text-red-500"}`}>
            {sourceId ? `ID: ${sourceId}` : "Could not extract a valid ID"}
          </p>
        )}
      </div>

      {/* Target */}
      <div className="mb-6">
        <label className="text-[#2D2B32] text-sm font-interMedium block pb-2">
          Copy To (Target)
        </label>
        <input
          type="text"
          className="w-full border border-[#D0D5DD] rounded-lg px-3.5 py-2.5 text-sm text-[#344054] placeholder:text-[#7F7D83] focus:outline-none focus:ring-1 focus:ring-[#1E0AF5] focus:border-[#1E0AF5]"
          placeholder="Paste target vijilization URL or ID"
          value={targetInput}
          onChange={(e) => setTargetInput(e.target.value)}
        />
        {targetInput && (
          <p className={`text-xs mt-1 ${targetId ? "text-green-600" : "text-red-500"}`}>
            {targetId ? `ID: ${targetId}` : "Could not extract a valid ID"}
          </p>
        )}
      </div>

      {/* Replicate Button */}
      <button
        onClick={handleReplicate}
        disabled={!canSubmit}
        className={`text-sm text-white font-interSemiBold bg-[#1E0AF5] hover:bg-[#113B98] rounded-lg px-5 py-2.5 ${
          !canSubmit ? "opacity-50 cursor-not-allowed" : ""
        }`}
      >
        {isSubmitting ? "Replicating..." : "Replicate"}
      </button>
    </div>
  );
};

export default ReplicateVijilization;
